import React, { useRef, useEffect, useState, useCallback } from 'react';
import jsQR from 'jsqr';
import { auth, db } from '../firebase';
import { addDoc, collection, doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { resolveQrFromFirestore } from '../utils/qrIndex';
import './QRScanner.css';

const SCAN_INTERVAL_MS = 350;
const SCANNER_INACTIVITY_MS = 90000;

const getScannerAudit = () => {
  const user = auth.currentUser;
  let role = '';
  try {
    role = localStorage.getItem('userRole') || '';
  } catch (_) {
    role = '';
  }

  return {
    scannedByUid: user?.uid || null,
    scannedByEmail: user?.email || null,
    scannedByName: user?.displayName || user?.email || null,
    scannedByRole: role || null,
  };
};

const QRScanner = () => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const scanLockRef = useRef(false);
  const ignoredQrValueRef = useRef(null);
  const feedbackTimeoutRef = useRef(null);
  const inactivityTimeoutRef = useRef(null);
  const lastScanAtRef = useRef(0);
  const [qrData, setQRData] = useState(null);
  const [error, setError] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [scanHistory, setScanHistory] = useState([]);
  const [isScanPaused, setIsScanPaused] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false); // Cambiado para manejar ambas acciones
  const [isCameraActive, setIsCameraActive] = useState(false);
  const [isScannerOn, setIsScannerOn] = useState(true); // Inicialmente el scanner esta encendido

  const validateQrEventIsActive = useCallback(async (data) => {
    const eventId = data?.eventId;
    if (!eventId) return true;
    const eventSnap = await getDoc(doc(db, 'events', eventId));
    return eventSnap.exists() && eventSnap.data()?.isActive === true;
  }, []);

  const startCamera = useCallback(async () => {
    console.log('Iniciando camara...');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: {
          facingMode: 'environment',
          width: { ideal: 640 },
          height: { ideal: 480 },
          frameRate: { ideal: 15, max: 24 },
        } 
      });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.onloadedmetadata = () => {
          videoRef.current.play();
          setIsCameraActive(true);
          console.log('Camara iniciada con exito');
        };
      }
      return stream;
    } catch (err) {
      console.error('Error al acceder a la camara:', err);
      setError('Error al acceder a la camara: ' + err.message);
      setIsCameraActive(false);
    }
  }, []);

  const stopCamera = useCallback(() => {
    if (videoRef.current && videoRef.current.srcObject) {
      videoRef.current.srcObject.getTracks().forEach(track => track.stop());
      videoRef.current.srcObject = null;
      setIsCameraActive(false);
      console.log('Camara detenida');
    }
  }, []);

  const clearInactivityTimer = useCallback(() => {
    if (inactivityTimeoutRef.current) {
      clearTimeout(inactivityTimeoutRef.current);
      inactivityTimeoutRef.current = null;
    }
  }, []);

  const pauseScannerForInactivity = useCallback(() => {
    clearInactivityTimer();
    stopCamera();
    scanLockRef.current = false;
    setIsScannerOn(false);
    setIsScanPaused(true);
    setError(null);
    setQRData(null);
    setFeedback({
      type: 'info',
      message: 'Scanner pausado por inactividad para ahorrar bateria.',
      actionLabel: 'Reactivar scanner',
      action: 'reactivate',
    });
  }, [clearInactivityTimer, stopCamera]);

  const resetInactivityTimer = useCallback(() => {
    clearInactivityTimer();
    if (!isCameraActive || !isScannerOn || isScanPaused || qrData || feedback) return;

    inactivityTimeoutRef.current = setTimeout(() => {
      pauseScannerForInactivity();
    }, SCANNER_INACTIVITY_MS);
  }, [
    clearInactivityTimer,
    feedback,
    isCameraActive,
    isScanPaused,
    isScannerOn,
    pauseScannerForInactivity,
    qrData,
  ]);

  useEffect(() => {
    let stream;
    if (isScannerOn) {
      startCamera().then(s => {
        stream = s;
      });
    }
  
    return () => {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
        setIsCameraActive(false);
        console.log('Camara detenida');
      }
    };
  }, [startCamera, isScannerOn]);

  useEffect(() => {
    return () => {
      if (feedbackTimeoutRef.current) {
        clearTimeout(feedbackTimeoutRef.current);
      }
      clearInactivityTimer();
    };
  }, [clearInactivityTimer]);

  useEffect(() => {
    resetInactivityTimer();
    return clearInactivityTimer;
  }, [clearInactivityTimer, resetInactivityTimer]);

  const toggleScanner = () => {
    if (isScannerOn) {
      clearInactivityTimer();
      setIsScannerOn(false);
      setIsScanPaused(false);
      setFeedback(null);
      stopCamera();
    } else {
      lastScanAtRef.current = 0;
      setIsScannerOn(true);
      setIsScanPaused(false);
      setFeedback(null);
      setError(null);
    }
  };

  const addScanHistory = useCallback((entry) => {
    const timestamp = new Date().toLocaleTimeString('es-AR', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });

    setScanHistory((currentHistory) => [
      { id: `${Date.now()}-${Math.random()}`, timestamp, ...entry },
      ...currentHistory,
    ].slice(0, 5));
  }, []);

  const ignoreQrTemporarily = useCallback((qrValue, duration = 2500) => {
    if (!qrValue) return;
    if (feedbackTimeoutRef.current) {
      clearTimeout(feedbackTimeoutRef.current);
    }

    ignoredQrValueRef.current = qrValue.toLowerCase();
    feedbackTimeoutRef.current = setTimeout(() => {
      ignoredQrValueRef.current = null;
      feedbackTimeoutRef.current = null;
    }, duration);
  }, []);

  const fetchQRData = useCallback(async (qrValue) => {
    console.log('Obteniendo datos del QR:', qrValue);
    try {
      const resolved = await resolveQrFromFirestore(db, qrValue);
      if (resolved) {
        if (resolved.eventId && resolved.eventIsActive !== true) {
          const detail = resolved.eventName
            ? `El QR pertenece al evento desactivado: ${resolved.eventName}.`
            : 'El QR pertenece a un evento desactivado.';
          resetInactivityTimer();
          setError(detail);
          setFeedback(null);
          setQRData(null);
          ignoreQrTemporarily(qrValue);
          addScanHistory({
            type: 'error',
            title: 'Evento desactivado',
            detail,
          });
          return;
        }

        console.log('Estableciendo datos del QR:', resolved);
        resetInactivityTimer();
        setQRData(resolved);
        setError(null);
        setFeedback(null);
        addScanHistory({
          type: resolved.isUsed ? 'used' : 'active',
          title: resolved.isUsed ? 'Ticket ya usado' : 'Ticket vigente',
          detail: `${resolved.model || 'Ticket'} - ${resolved.nombre || 'Sin nombre'}`,
        });
      } else {
        console.log('Codigo QR no encontrado en la base de datos');
        resetInactivityTimer();
        setError('El c\u00f3digo QR no se encuentra en la base de datos');
        setFeedback(null);
        setQRData(null);
        ignoreQrTemporarily(qrValue);
        addScanHistory({
          type: 'error',
          title: 'QR no encontrado',
          detail: 'No existe en la base de datos',
        });
      }
    } catch (err) {
      console.error('Error al obtener datos del QR:', err);
      resetInactivityTimer();
      setError(`Error al obtener datos del QR: ${err.message}`);
      setFeedback(null);
      setQRData(null);
      ignoreQrTemporarily(qrValue);
      addScanHistory({
        type: 'error',
        title: 'Error al leer QR',
        detail: err.message,
      });
    }
  }, [addScanHistory, ignoreQrTemporarily, resetInactivityTimer]);

  const showScanFeedback = useCallback((message, qrValueToIgnore) => {
    if (feedbackTimeoutRef.current) {
      clearTimeout(feedbackTimeoutRef.current);
      feedbackTimeoutRef.current = null;
    }

    ignoredQrValueRef.current = qrValueToIgnore?.toLowerCase() || null;
    setError(null);
    setIsScanPaused(true);
    setFeedback({ type: 'success', message, actionLabel: 'Escanear otro', action: 'scanNext' });
  }, []);

  const handleScanNext = () => {
    ignoredQrValueRef.current = null;
    scanLockRef.current = false;
    lastScanAtRef.current = 0;
    setFeedback(null);
    setError(null);
    setQRData(null);
    setIsScanPaused(false);
    resetInactivityTimer();
  };

  const handleReactivateScanner = () => {
    ignoredQrValueRef.current = null;
    scanLockRef.current = false;
    lastScanAtRef.current = 0;
    setFeedback(null);
    setError(null);
    setQRData(null);
    setIsScannerOn(true);
    setIsScanPaused(false);
  };

  const handleFeedbackAction = () => {
    if (feedback?.action === 'reactivate') {
      handleReactivateScanner();
      return;
    }

    handleScanNext();
  };

  const scanQRCode = useCallback(() => {
    if (isScanPaused || qrData) {
      return;
    }

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const now = Date.now();
    const canProcessFrame = now - lastScanAtRef.current >= SCAN_INTERVAL_MS && !scanLockRef.current;

    if (video && canvas && video.readyState === video.HAVE_ENOUGH_DATA && canProcessFrame) {
      lastScanAtRef.current = now;
      canvas.height = video.videoHeight;
      canvas.width = video.videoWidth;
      const context = canvas.getContext('2d');
      context.drawImage(video, 0, 0, canvas.width, canvas.height);
      const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
      const code = jsQR(imageData.data, imageData.width, imageData.height);

      if (code) {
        const scannedQrValue = code.data;
        const normalizedScannedQrValue = scannedQrValue.toLowerCase();

        if (normalizedScannedQrValue === ignoredQrValueRef.current) {
          requestAnimationFrame(scanQRCode);
          return;
        }

        if (!scanLockRef.current) {
          console.log('Codigo QR detectado:', scannedQrValue);
          resetInactivityTimer();
          scanLockRef.current = true;
          fetchQRData(scannedQrValue).finally(() => {
            scanLockRef.current = false;
          });
        }
      }
    }

    if (!qrData) {
      requestAnimationFrame(scanQRCode);
    }
  }, [fetchQRData, qrData, isScanPaused, resetInactivityTimer]);

  useEffect(() => {
    let animationFrameId;
    if (isCameraActive && !isScanPaused) {
      console.log('Iniciando escaneo de QR');
      animationFrameId = requestAnimationFrame(scanQRCode);
    }
    return () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, [scanQRCode, isCameraActive, isScanPaused]);

  const handleMarkUse = async () => {
    console.log('Intentando marcar como usado. Datos del QR:', qrData);
    if (!qrData || !qrData.docId || !qrData.qrValue || !qrData.cartItems || !Array.isArray(qrData.cartItems)) {
      console.error('Datos del QR invalidos');
      setError('No se puede marcar el uso: datos del QR no v\u00e1lidos');
      return;
    }
  
    setIsProcessing(true);
  
    try {
      const eventStillActive = await validateQrEventIsActive(qrData);
      if (!eventStillActive) {
        setError(qrData.eventName
          ? `El QR pertenece al evento desactivado: ${qrData.eventName}.`
          : 'El QR pertenece a un evento desactivado.');
        return;
      }

      const docRef = doc(db, qrData.collectionName, qrData.docId);
      const localTimestamp = new Date().toLocaleString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires' });
      const scannerAudit = getScannerAudit();
      
      const updatedCartItems = qrData.cartItems.map(item =>
        item.qrValue === qrData.qrValue 
          ? {
              ...item,
              isUsed: true,
              usedTimestamp: localTimestamp,
              usedAt: new Date().toISOString(),
              ...scannerAudit,
            }
          : item
      );
  
      console.log('Actualizando documento con ID:', qrData.docId);
      console.log('Items actualizados del carrito:', updatedCartItems);
  
      await updateDoc(docRef, { 
        cartItems: updatedCartItems,
        lastUpdated: serverTimestamp() 
      });
      await addDoc(collection(db, 'qr_scan_logs'), {
        action: 'markUsed',
        scannedAt: new Date(),
        localTimestamp,
        qrValue: qrData.qrValue,
        parentCollection: qrData.collectionName,
        parentDocId: qrData.docId,
        orderNumber: qrData.orderNumber || null,
        model: qrData.model || null,
        productCollection: qrData.collection || qrData.productCollection || qrData.catalogCollection || 'products',
        quantity: Number(qrData.quantity || 1),
        nombre: qrData.nombre || null,
        saleCreatedByUid: qrData.saleCreatedByUid || null,
        saleCreatedByEmail: qrData.saleCreatedByEmail || null,
        saleCreatedByName: qrData.saleCreatedByName || null,
        saleCreatedByRole: qrData.saleCreatedByRole || null,
        ...scannerAudit,
      });
  
      console.log('Codigo QR marcado como usado con timestamp:', localTimestamp);
      addScanHistory({
        type: 'success',
        title: 'Ticket marcado',
        detail: `${qrData.model || 'Ticket'} - ${qrData.nombre || 'Sin nombre'}`,
      });
      showScanFeedback('Ticket marcado correctamente. Ya pod\u00e9s escanear otro QR.', qrData.qrValue);
      setQRData(null); // Cerrar la modal y esperar confirmacion para seguir escaneando
    } catch (err) {
      console.error('Error al actualizar el estado isUsed:', err);
      setError(`Error al actualizar el estado: ${err.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCancelUse = () => {
    ignoreQrTemporarily(qrData?.qrValue);
    resetInactivityTimer();
    setQRData(null);
    setError(null);
    console.log('Reiniciando escaneo de QR');
    // No es necesario reiniciar la camara, ya que nunca la detenemos
  };

  const closeModal = () => {
    ignoreQrTemporarily(qrData?.qrValue);
    resetInactivityTimer();
    setQRData(null);
    setError(null);
    console.log('Modal cerrado, reanudando escaneo');
    // El escaneo se reanuda automaticamente porque qrData es null
  };

  return (
    <div className="qr-scanner-container">
      <h2 className="qr-scanner-title">Esc&aacute;ner de QR</h2>
      
      <div className="qr-scanner-view">
        <video ref={videoRef} autoPlay playsInline style={{ display: 'none' }} />
        <canvas ref={canvasRef} />
        {!isCameraActive && <div className="qr-scanner-loading">Scanner apagado</div>}
        {feedback && (
          <div className={`qr-scanner-feedback ${feedback.type}`}>
            <p>{feedback.message}</p>
            {feedback.actionLabel && (
              <button className="qr-feedback-button" onClick={handleFeedbackAction}>
                {feedback.actionLabel}
              </button>
            )}
          </div>
        )}
        {error && <div className="qr-scanner-error">{error}</div>}
      </div>
      {qrData && (
        <div className="qr-modal-overlay" onClick={closeModal}>
          <div className={`qr-modal ${qrData.isUsed ? 'used' : 'active'}`} onClick={(e) => e.stopPropagation()}>
            <div className={`qr-verdict ${qrData.isUsed ? 'used' : 'active'}`}>
              <span className="qr-verdict-label">Estado del ticket</span>
              <h3>{qrData.isUsed ? 'Ticket ya usado' : 'Ticket vigente'}</h3>
              <p>
                {qrData.isUsed
                  ? 'No habilitar ingreso sin revisar la fecha de uso.'
                  : 'Ticket valido para marcar ingreso.'}
              </p>
            </div>
            <div className="qr-modal-content">
              <div className="qr-detail-row">
                <span>Nombre</span>
                <strong>{qrData.nombre || 'Sin nombre'}</strong>
              </div>
              <div className="qr-detail-row">
                <span>N&uacute;mero de ticket</span>
                <strong>{qrData.orderNumber || 'Sin numero'}</strong>
              </div>
              <div className="qr-detail-row">
                <span>Producto</span>
                <strong>{qrData.model || 'Ticket'}</strong>
              </div>
              <div className="qr-detail-row">
                <span>Cantidad</span>
                <strong>{qrData.quantity}</strong>
              </div>
              {qrData.usedTimestamp && (
                <div className="qr-detail-row qr-used-timestamp">
                  <span>Usado el</span>
                  <strong>{qrData.usedTimestamp}</strong>
                </div>
              )}
            </div>
            <div className="qr-modal-actions">
              {!qrData.isUsed && (
                <button
                  className="qr-button mark"
                  onClick={handleMarkUse}
                  disabled={isProcessing}
                >
                  {isProcessing ? 'Procesando...' : 'Marcar ingreso'}
                </button>
              )}
              <button
                className="qr-button cancel"
                onClick={handleCancelUse}
              >
                Cerrar sin cambios
              </button>
            </div>
          </div>
        </div>
      )}
      <br/>
      <div className="scanner-toggle">
        <label className="switch">
          <input type="checkbox" checked={isScannerOn} onChange={toggleScanner} />
          <span className="slider round"></span>
        </label>
      </div>
      <br/>
      <span>{isScannerOn ? 'Esc\u00e1ner encendido' : 'Esc\u00e1ner apagado'}</span>
      {scanHistory.length > 0 && (
        <section className="qr-scan-history" aria-label="Ultimos escaneos">
          <h3>Ultimos escaneos</h3>
          <ul>
            {scanHistory.map((entry) => (
              <li key={entry.id} className={`qr-scan-history-item ${entry.type}`}>
                <span className="qr-scan-history-time">{entry.timestamp}</span>
                <div>
                  <strong>{entry.title}</strong>
                  <p>{entry.detail}</p>
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
};

export default QRScanner;
